import * as THREE from "three";
import type { TargetRange } from "../world/targets";
import type { ScreenShake } from "../core/screenshake";
import { Audio } from "../sfx/audio";

// Three-layer pooled explosion:
//   flash  — white/yellow burst  (0.14 s, fast expand)
//   fire   — orange fireball     (0.52 s, swell then shrink)
//   smoke  — dark column         (1.05 s, rises briefly then clears)

const POOL_SIZE = 12;

const FLASH_DUR = 0.14;
const FIRE_DUR  = 0.52;
const SMOKE_DUR = 1.05;

type ExplosionSlot = {
  flash: THREE.Mesh;
  fire:  THREE.Mesh;
  smoke: THREE.Mesh;
  ring: THREE.Mesh;
  light: THREE.PointLight;
  weaponId: string;
  maxRadius: number;
  flashLife: number;
  fireLife:  number;
  smokeLife: number;
  active: boolean;
};

const flashMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false });
const fireMat  = new THREE.MeshBasicMaterial({ color: 0xff5500, transparent: true, opacity: 0, depthWrite: false });
const smokeMat = new THREE.MeshBasicMaterial({ color: 0x202020, transparent: true, opacity: 0, depthWrite: false });
const ringMat  = new THREE.MeshBasicMaterial({ color: 0xffc777, transparent: true, opacity: 0, depthWrite: false, side: THREE.DoubleSide });

export class ExplosionManager {
  private readonly pool: ExplosionSlot[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly targets: TargetRange,
    private readonly shaker: ScreenShake,
  ) {
    const ballGeom = new THREE.SphereGeometry(1, 10, 10);
    ballGeom.scale(1, 0.75, 1);
    const ringGeom = new THREE.RingGeometry(0.82, 1, 24);

    for (let i = 0; i < POOL_SIZE; i++) {
      const flash = new THREE.Mesh(ballGeom, flashMat.clone());
      const fire  = new THREE.Mesh(ballGeom, fireMat.clone());
      const smoke = new THREE.Mesh(ballGeom, smokeMat.clone());
      const ring  = new THREE.Mesh(ringGeom, ringMat.clone());
      flash.userData["isVFX"] = fire.userData["isVFX"] = smoke.userData["isVFX"] = ring.userData["isVFX"] = true;
      flash.visible = fire.visible = smoke.visible = ring.visible = false;
      ring.rotation.x = -Math.PI / 2;

      const light = new THREE.PointLight(0xff8800, 0, 0);
      flash.add(light);

      scene.add(flash, fire, smoke, ring);
      this.pool.push({ flash, fire, smoke, ring, light, weaponId: "autocannon", maxRadius: 0, flashLife: 0, fireLife: 0, smokeLife: 0, active: false });
    }
  }

  spawn(center: THREE.Vector3, radius: number, weaponId = "autocannon"): void {
    const slot = this.pool.find((s) => !s.active);
    if (!slot) return;

    const pos = center.clone();
    pos.y = Math.max(pos.y, 0.3);

    slot.flash.position.copy(pos);
    slot.fire.position.copy(pos);
    slot.smoke.position.copy(pos);
    slot.ring.position.copy(pos);
    slot.ring.position.y += 0.05;

    slot.maxRadius  = radius;
    slot.weaponId   = weaponId;
    slot.flashLife  = FLASH_DUR;
    slot.fireLife   = FIRE_DUR;
    slot.smokeLife  = SMOKE_DUR;
    slot.active     = true;

    const isHowitzer = weaponId === "howitzer";
    slot.flash.scale.set(radius * (isHowitzer ? 3.3 : 2.5), radius * (isHowitzer ? 1.9 : 1.25), radius * (isHowitzer ? 3.3 : 2.5));
    slot.fire.scale.setScalar(radius * (isHowitzer ? 1.8 : 1.35));
    slot.smoke.scale.set(radius * (isHowitzer ? 1.45 : 1.0), radius * (isHowitzer ? 1.0 : 0.72), radius * (isHowitzer ? 1.45 : 1.0));
    slot.ring.scale.setScalar(radius * (isHowitzer ? 1.45 : 1.0));
    slot.flash.visible = slot.fire.visible = slot.smoke.visible = slot.ring.visible = true;
    slot.light.distance = radius * 5;
    slot.light.color.set(isHowitzer ? 0xff9f52 : 0xffdd99);

    this.targets.hitInRadius(center, radius);

    const large = radius >= 10;
    this.shaker.trigger(large ? 0.9 : 0.38, large ? 0.55 : 0.25, large ? "punch" : "rumble");
    Audio.explosion(large);
  }

  update(dt: number): void {
    for (const s of this.pool) {
      if (!s.active) continue;

      // Flash — fast bright burst
      if (s.flashLife > 0) {
        s.flashLife -= dt;
        const t = Math.max(0, s.flashLife / FLASH_DUR);
        const isHowitzer = s.weaponId === "howitzer";
        s.flash.visible = t > 0.01;
        (s.flash.material as THREE.MeshBasicMaterial).opacity = t * 0.95;
        (s.flash.material as THREE.MeshBasicMaterial).color.set(isHowitzer ? 0xffe0b2 : 0xfff4d0);
        s.flash.scale.set(
          s.maxRadius * ((isHowitzer ? 3.3 : 2.5) + (1 - t) * (isHowitzer ? 1.9 : 1.1)),
          s.maxRadius * ((isHowitzer ? 1.9 : 1.25) + (1 - t) * (isHowitzer ? 0.9 : 0.45)),
          s.maxRadius * ((isHowitzer ? 3.3 : 2.5) + (1 - t) * (isHowitzer ? 1.9 : 1.1)),
        );
        s.light.intensity = t * (isHowitzer ? 16 : 11);
        s.ring.visible = true;
        (s.ring.material as THREE.MeshBasicMaterial).opacity = t * (isHowitzer ? 0.75 : 0.52);
        (s.ring.material as THREE.MeshBasicMaterial).color.set(isHowitzer ? 0xffbf7c : 0xffefba);
        s.ring.scale.setScalar(s.maxRadius * ((isHowitzer ? 1.45 : 1.0) + (1 - t) * (isHowitzer ? 4.8 : 2.8)));
      } else {
        s.flash.visible = false;
        s.ring.visible = false;
        s.light.intensity = 0;
      }

      // Fireball — swells then contracts
      if (s.fireLife > 0) {
        s.fireLife -= dt;
        const t = Math.max(0, s.fireLife / FIRE_DUR);
        const isHowitzer = s.weaponId === "howitzer";
        s.fire.visible = true;
        const phase = Math.sin(t * Math.PI); // peaks mid-life
        (s.fire.material as THREE.MeshBasicMaterial).opacity = t * 0.88;
        (s.fire.material as THREE.MeshBasicMaterial).color.set(isHowitzer ? 0xff6530 : 0xffb04a);
        s.fire.scale.set(
          s.maxRadius * ((isHowitzer ? 1.8 : 1.25) + phase * (isHowitzer ? 2.6 : 1.5)),
          s.maxRadius * ((isHowitzer ? 1.1 : 0.8) + phase * (isHowitzer ? 1.45 : 0.7)),
          s.maxRadius * ((isHowitzer ? 1.8 : 1.25) + phase * (isHowitzer ? 2.6 : 1.5)),
        );
      } else {
        s.fire.visible = false;
      }

      // Smoke — expands and rises
      if (s.smokeLife > 0) {
        s.smokeLife -= dt;
        const t = Math.max(0, s.smokeLife / SMOKE_DUR);
        const expand = 1 - t;
        const isHowitzer = s.weaponId === "howitzer";
        s.smoke.visible = true;
        (s.smoke.material as THREE.MeshBasicMaterial).opacity = t * t * (isHowitzer ? 0.42 : 0.26);
        (s.smoke.material as THREE.MeshBasicMaterial).color.set(isHowitzer ? 0x2b241e : 0x38322d);
        s.smoke.scale.set(
          s.maxRadius * ((isHowitzer ? 1.4 : 0.95) + expand * (isHowitzer ? 3.1 : 1.3)),
          s.maxRadius * ((isHowitzer ? 1.0 : 0.62) + expand * (isHowitzer ? 1.45 : 0.55)),
          s.maxRadius * ((isHowitzer ? 1.4 : 0.95) + expand * (isHowitzer ? 3.1 : 1.3)),
        );
        s.smoke.position.y += s.maxRadius * (isHowitzer ? 0.22 : 0.12) * dt;
      } else {
        s.smoke.visible = false;
      }

      if (s.flashLife <= 0 && s.fireLife <= 0 && s.smokeLife <= 0) s.active = false;
    }
  }

  dispose(): void {
    for (const s of this.pool) this.scene.remove(s.flash, s.fire, s.smoke, s.ring);
  }
}
